import React, { FC, PropsWithChildren, useState } from "react";

import classNames from "classnames";
import { useTranslation, Trans } from "react-i18next";
import "./FAQ.scss";

import { useGetSettings } from "@api";

import {
  Content,
  Icon,
  Icons,
  ImageComponent,
  Tag,
  Tags,
  Text,
} from "@ui/elements";
import { Stack } from "@ui/layout";

type FAQItemProps = PropsWithChildren<{
  title: string;
  icon?: Icons;
}>;

export const FAQItem: FC<FAQItemProps> = ({ children, title, icon }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={classNames(["faq-item", { "is-open": open }])}>
      <header className="faq-item__header" onClick={() => setOpen(!open)}>
        <div>
          <Stack gap="xs">
            {icon && <Icon icon={icon} />}
            <Text>{title}</Text>
          </Stack>
        </div>
        <div>
          <Icon icon={open ? "Minus" : "Plus"} size="small" />
        </div>
      </header>
      <div className="faq-item__content">{children}</div>
    </div>
  );
};

export const FAQ = () => {
  const { data: settings } = useGetSettings();
  const { t } = useTranslation();

  return (
    <Stack direction="column">
      <Stack direction="column">
        {/* FAQ Item 1 */}
        <FAQItem title={t("faq.singleOrParlay.title")}>
          <Content>
            <p>{t("faq.singleOrParlay.description")}</p>
            <blockquote>
              <Trans i18nKey="faq.singleOrParlay.details" />
            </blockquote>
          </Content>
        </FAQItem>

        {/* FAQ Item 2 */}
        <FAQItem title={t("faq.howMuchBet.title")}>
          <Content>
            <p>
              <Trans i18nKey="faq.howMuchBet.description" />
            </p>
            <p>{t("faq.howMuchBet.additional")}</p>
          </Content>
        </FAQItem>

        {/* FAQ Item 3 */}
        <FAQItem title={t("faq.whenPredictionsAvailable.title")}>
          <Content>
            <p>
              <Trans i18nKey="faq.whenPredictionsAvailable.description" />
            </p>
            <p>{t("faq.whenPredictionsAvailable.additional")}</p>
          </Content>
        </FAQItem>

        {/* FAQ Item 4 */}
        <FAQItem title={t("faq.supportedLeagues.title")}>
          <Content>
            <Tags>
              {settings?.leagues.map((league) => (
                <Tag key={`faq-league-item-${league.league.id}`} size="medium">
                  <Stack gap="sm">
                    <ImageComponent size={16} src={league.league.logo} />
                    <span>{league.league.name}</span>
                  </Stack>
                </Tag>
              ))}
            </Tags>
            <br />
            <p>
              <Trans i18nKey="faq.supportedLeagues.description" />
            </p>
          </Content>
        </FAQItem>

        {/* FAQ Item 5 */}
        <FAQItem title={t("faq.tipCategories.title")}>
          <Content>
            <p>{t("faq.tipCategories.description")}</p>
            <ul>
              <li>{t("faq.tipCategories.categories.matchWinner")}</li>
              <li>{t("faq.tipCategories.categories.doubleChance")}</li>
              <li>{t("faq.tipCategories.categories.bothTeamsToScore")}</li>
              <li>{t("faq.tipCategories.categories.goalsOverUnder")}</li>
            </ul>
            <p>
              <Trans i18nKey="faq.tipCategories.additional" />
            </p>
          </Content>
        </FAQItem>

        {/* FAQ Item 6 */}
        <FAQItem title={t("faq.teamEvaluation.title")}>
          <Content>
            <p>
              <Trans i18nKey="faq.teamEvaluation.description" />
            </p>
            <ul>
              <li>{t("faq.teamEvaluation.factors.motivation")}</li>
              <li>{t("faq.teamEvaluation.factors.injuryImpact")}</li>
              <li>{t("faq.teamEvaluation.factors.recentForm")}</li>
              <li>{t("faq.teamEvaluation.factors.tacticalAdvantage")}</li>
            </ul>
            <p>{t("faq.teamEvaluation.additional")}</p>
          </Content>
        </FAQItem>
      </Stack>
    </Stack>
  );
};
